/* eslint-disable consistent-return */
import React, { useEffect, useMemo } from 'react';
import { parse, isWithinInterval } from 'date-fns';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text,
} from '@chakra-ui/react';
import {
  every,
  find,
  flatMap,
  forEach,
  intersection,
  map,
  size,
  union,
  values,
} from 'lodash-es';
import { useFormState } from 'react-final-form';
import { Control } from 'formComponents/Control';
import {
  DetailsService,
  ModalPage,
  NotFoundAlert,
  QuickSearch,
  ServiceBox,
} from 'components';
import {
  ServiceGroupsInfo,
  ServiceInfo,
  IntervalsData,
  SlotsPerMaster,
} from 'types';
import {
  getMastersSkills,
  parseTimeSlots,
  convertISO8601ToSeconds,
  getDurationMessage,
} from 'utils';

export const Service: React.FC<{
  name: string;
  widgetId?: string;
  serviceGroups: ServiceGroupsInfo;
  possibleMastersIds?: string[];
  possibleMastersByDayIds?: string[];
  setPossibleMastersIds: (ids?: string[]) => void;
  slotsPerMaster?: SlotsPerMaster[];
  setSlotsPerMaster: (slotsPerMaster?: SlotsPerMaster[]) => void;
  duration: string;
  intervalsFavor?: IntervalsData;
  isFirstStepDate?: boolean;
  maxVisitDuration: string;
}> = ({
  name,
  widgetId,
  serviceGroups: serviceGroupsInfo,
  possibleMastersIds,
  possibleMastersByDayIds,
  setPossibleMastersIds,
  slotsPerMaster,
  setSlotsPerMaster,
  duration,
  intervalsFavor,
  isFirstStepDate,
  maxVisitDuration,
}) => {
  const {
    values: { favorIds, favorCounts, employeeId, date },
  } = useFormState<{
    favorIds?: string[];
    favorCounts?: Record<string, number>;
    employeeId?: string;
    date?: Date;
  }>();

  const mastersSkills = useMemo(
    () => getMastersSkills(serviceGroupsInfo),
    [serviceGroupsInfo],
  );

  const slotDuration = convertISO8601ToSeconds(duration);
  const maxVisitDurationSeconds = maxVisitDuration
    ? convertISO8601ToSeconds(maxVisitDuration)
    : 0;

  const maxDurationMaster = (emplId: string, favorId: string): number => {
    const intervals = intervalsFavor?.find(
      (slot) => slot.favorId === favorId,
    )?.intervals;
    const timesMaster = slotsPerMaster?.find(
      (slot) => slot.employeeId === emplId,
    )?.times;
    const times = timesMaster?.filter((time) => {
      let check = true;
      intervals?.forEach((interval) => {
        const start = parse(interval?.start, 'HH:mm', new Date());
        const end = parse(interval?.end, 'HH:mm', new Date());
        const checkTime = parse(time, 'HH:mm', new Date());
        if (isWithinInterval(checkTime, { start, end })) {
          check = false;
        }
      });
      return check;
    });
    if (!times) {
      return 0;
    }
    const parseTimes = parseTimeSlots(times);
    let count = slotDuration;
    let maxDuration = 0;
    parseTimes.reverse().forEach((time, index) => {
      if (count > maxDuration) {
        maxDuration = count;
      }
      if (
        parseTimes[index + 1] &&
        (time.getTime() - parseTimes[index + 1].getTime()) / 1000 > slotDuration
      ) {
        count = slotDuration;
      } else {
        count += slotDuration;
      }
    });
    return maxDuration;
  };

  // Быстрый поиск
  const [filter, setFilter] = React.useState('');

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value);

  const serviceGroups = useMemo(() => {
    const groups: ServiceGroupsInfo = {};
    const employeeSelected = employeeId !== 'any';

    forEach(serviceGroupsInfo, (services, key) => {
      const filteredServices = services.filter((service) => {
        if (service.type && service.type !== 'individual') {
          return false;
        }
        const filterBySearch = service.name
          .toLowerCase()
          .includes(filter.toLowerCase());

        const filterBySkills =
          employeeSelected && employeeId
            ? mastersSkills[employeeId]?.includes(service.id)
            : true;

        return filterBySearch && filterBySkills;
      });
      if (filteredServices.length) groups[key] = filteredServices;
    });
    return groups;
  }, [filter, serviceGroupsInfo, employeeId, mastersSkills]);

  const flattenServices = useMemo(
    () => flatMap(serviceGroups),
    [serviceGroups],
  );

  const selectedServices = useMemo(
    () => flattenServices.filter((service) => favorIds?.includes(service.id)),
    [flattenServices, favorIds],
  );

  const durationSelectedServices = useMemo(
    () =>
      selectedServices.reduce((sumDuration, currentService) => {
        const count = favorCounts?.[currentService.id] ?? 1;
        return sumDuration + currentService.duration * count;
      }, 0),
    [favorCounts, selectedServices],
  );

  const isShowDuration = every(
    selectedServices,
    (service) => service.showDuration,
  );

  const durationSelectedServicesText = isShowDuration
    ? getDurationMessage(durationSelectedServices)
    : '';

  const isShowPrice = every(
    selectedServices,
    (service) => service.showPrice && !service.priceInterval,
  );

  const priceSelectedServicesText = isShowPrice
    ? `${selectedServices.reduce((sumPrice, currentService) => {
        const count = favorCounts?.[currentService.id] ?? 1;
        return sumPrice + currentService.price * count;
      }, 0)} ₽`
    : '';

  useEffect(() => {
    if (selectedServices.length) {
      setPossibleMastersIds(
        intersection(
          ...selectedServices.map((service) => service.employee || []),
        ),
      );
    } else {
      setPossibleMastersIds();
    }
  }, [selectedServices, setPossibleMastersIds]);

  useEffect(() => {
    if (!isFirstStepDate && !date) {
      setSlotsPerMaster();
    }
  }, [isFirstStepDate, date]);

  const mastersIds = useMemo(() => {
    return employeeId && employeeId !== 'any'
      ? [employeeId]
      : possibleMastersIds;
  }, [employeeId, possibleMastersIds]);

  const possibleServicesIds = useMemo(() => {
    if (mastersIds?.length) {
      return mastersIds.reduce(
        (servicesIds: string[], masterId) =>
          union(servicesIds, mastersSkills[masterId]),
        [],
      );
    }
    return null;
  }, [employeeId, possibleMastersIds, mastersSkills]);

  const [itemId, setItemId] = React.useState<string | null>(null);
  const item = useMemo(
    () => find(flattenServices, ({ id }) => id === itemId),
    [flattenServices, itemId],
  );
  const resetItemId = () => setItemId(null);

  const possibleService = (service: ServiceInfo): boolean => {
    return possibleServicesIds
      ? possibleServicesIds.includes(service.id)
      : true;
  };

  const isServiceAvailable = (service: ServiceInfo): boolean => {
    const maxPossibleServicesDuration = Math.max(
      ...values(
        service.employee.map(
          (emplId) =>
            (mastersIds?.includes(emplId) &&
              maxDurationMaster(emplId, service.id)) ||
            0,
        ),
      ),
    );
    const maxPossibleDuration = maxVisitDurationSeconds
      ? Math.min(maxPossibleServicesDuration, maxVisitDurationSeconds)
      : maxPossibleServicesDuration;
    const servicesDuration = service.duration + durationSelectedServices;
    return maxPossibleDuration ? servicesDuration <= maxPossibleDuration : true;
  };

  const disabledService = (service: ServiceInfo): boolean => {
    return !isServiceAvailable(service) || !possibleService(service);
  };

  const warningService = (service: ServiceInfo) => {
    if (
      (possibleMastersByDayIds &&
        !intersection(possibleMastersByDayIds, service.employee)?.length) ||
      !isServiceAvailable(service)
    ) {
      return 'В выбранный день отсутствует интервал времени, достаточный для оказания услуги в выбранном количестве';
    }
    if (!possibleService(service)) {
      return 'Услуга несовместима с выбранными';
    }
  };

  const showInfo = (id: string) => {
    setItemId(id);
  };

  return (
    <>
      <ModalPage isOpen={!!item} title="Об услуге" onClose={resetItemId}>
        {item && (
          <DetailsService
            service={item}
            widgetId={widgetId}
            message={warningService(item)}
          />
        )}
      </ModalPage>

      <div>
        <QuickSearch
          placeholder="Поиск услуги"
          value={filter}
          onChange={handleFilterChange}
        />
        {flattenServices.length ? (
          <Control name={name}>
            {size(serviceGroups) === 1 &&
            Object.keys(serviceGroups)[0] === 'Прочие услуги' ? (
              map(Object.values(serviceGroups)[0], (service: ServiceInfo) => {
                const { id } = service;
                const isShowWarning = disabledService(service);
                const isSelected = !!favorIds?.includes(service.id);
                const isDisabled = isShowWarning && !isSelected;
                return (
                  <ServiceBox
                    key={id}
                    id={id}
                    name={name}
                    disabled={isDisabled}
                    showWarning={isShowWarning}
                    otherServicesOnly
                    service={service}
                    showInfo={showInfo}
                  />
                );
              })
            ) : (
              <Accordion
                allowToggle
                borderRadius={8}
                borderColor="grayBorder"
                bgColor="white"
                index={size(serviceGroups) === 1 ? 0 : undefined}
              >
                {map(serviceGroups, (services: ServiceInfo[], groupTitle) => {
                  return (
                    <AccordionItem
                      key={groupTitle}
                      borderWidth={0}
                      _notLast={{
                        borderBottomWidth: '1px',
                      }}
                      _last={{ borderWidth: '0' }}
                    >
                      {({ isExpanded }) => (
                        <>
                          <AccordionButton
                            _focus={{ borderWidth: '0' }}
                            _hover={{
                              bgColor: 'white',
                            }}
                          >
                            <Text
                              color="black"
                              textStyle="h4"
                              flex="1"
                              textAlign="left"
                              translate="no"
                            >
                              {groupTitle}
                            </Text>
                            <AccordionIcon />
                          </AccordionButton>
                          {isExpanded && (
                            <AccordionPanel p={0} borderTopWidth="1px">
                              {map(services, (service: ServiceInfo) => {
                                const { id } = service;
                                const isShowWarning = disabledService(service);
                                const isSelected = !!favorIds?.includes(
                                  service.id,
                                );
                                const isDisabled = isShowWarning && !isSelected;
                                return (
                                  <ServiceBox
                                    key={id}
                                    id={id}
                                    name={name}
                                    disabled={isDisabled}
                                    showWarning={isShowWarning}
                                    service={service}
                                    showInfo={showInfo}
                                  />
                                );
                              })}
                            </AccordionPanel>
                          )}
                        </>
                      )}
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
            {selectedServices.length && (isShowPrice || isShowDuration) ? (
              <Text
                color="black"
                textStyle="body2"
                mt={4}
                mb={2}
                textAlign={{ md: 'end' }}
                whiteSpace="nowrap"
              >
                <Text as="span" mr={2}>
                  Итого:
                </Text>
                <Box as="span" float={{ base: 'right', md: 'unset' }}>
                  {isShowPrice && (
                    <Text as="span" mr={isShowDuration ? 4 : 0}>
                      {priceSelectedServicesText}
                    </Text>
                  )}
                  {isShowDuration && (
                    <Text as="span">{durationSelectedServicesText}</Text>
                  )}
                </Box>
              </Text>
            ) : null}
          </Control>
        ) : (
          <NotFoundAlert text="Услуга не найдена" />
        )}
      </div>
    </>
  );
};
